import _ from 'lodash';
import moment from 'moment';

import * as api from 'assets/js/api';
import auth from 'assets/js/auth';

import { IntTooBig } from 'assets/js/numbers';

const ACTIVITY_DEBOUNCE_MS = 500;
const DEFAULT_DURATION_MS = 60 * 30 * 1000; // 30 minutes.
const LOCAL_STORAGE_ANTICIPATED_LOGOUT_TIME_KEY = 'QRESERVE_AUTO_LOGOUT_TS';
const LOGOUT_TS_TOLERANCE = 5; // seconds

export default {
  methods: {
    // autoInactivityMaybeLogout checks the expiration time in local storage
    // against the current time and only logs out in the event that all
    // open windows/tabs have not updated the expiration time.
    autoInactivityMaybeLogout() {
      const tsString = window.localStorage.getItem(
        LOCAL_STORAGE_ANTICIPATED_LOGOUT_TIME_KEY);
      const ts = parseInt(tsString, 10);
      if (moment().unix() > ts - LOGOUT_TS_TOLERANCE) {
        this.signOut();
      } else {
        // Respawn the watcher thread but do NOT set a new local store
        // expiry time.
        this.spawnAutoInactivityLogoutThread(true);
      }
    },

    // spawnAutoInactivityLogoutThread spawns an automatic logout thread,
    // ensuring that the client automatically logs out after a specified time
    // of inactivity. With activity, this thread is respawned repeatedly.
    spawnAutoInactivityLogoutThread(noSetLocalStore) {
      if (!this.$store.state.auth?.authUser) return;
      const timeout = this.$store.state.auth?.authUser.inactivity_timeout;
      let durationMs = DEFAULT_DURATION_MS;
      try {
        durationMs = parseInt(timeout, 10) * 1000;
      } catch (err) {
        // This shouldn't happen, but just in case, we put a 30 minute timout
        // to be extra careful and we can display the error here.
        console.error(err);
      }
      if (IntTooBig(durationMs)) {
        // Just in case, go back to 30 minutes.
        durationMs = DEFAULT_DURATION_MS;
      }

      window.clearTimeout(_.get(window, 'autoInactivityLogoutThread'));
      if (!IntTooBig(durationMs)) {
        // Write the current timestamp to local storage.
        const ts = moment().add(durationMs, 'ms').unix();
        if (noSetLocalStore !== true) {
          window.localStorage.setItem(LOCAL_STORAGE_ANTICIPATED_LOGOUT_TIME_KEY,
            String(ts));
        }

        const threadId = window.setTimeout(
          () => {
            window.clearTimeout(_.get(window, 'autoInactivityLogoutThread'));
            this.autoInactivityMaybeLogout();
          },
          durationMs,
        );
        _.set(window, 'autoInactivityLogoutThread', threadId);
      }
    },

    // spawnAutoLogoutThread spawns an automatic logout thread upon login,
    // ensuring that the client automatically logs out when the session
    // expires.
    spawnAutoLogoutThread() {
      const now = moment().utc();
      const then = moment(this.$store.state.auth.expires);
      const durationMs = moment
        .duration(then.diff(now))
        .asMilliseconds();
      window.clearTimeout(_.get(window, 'autoLogoutThread'));
      if (!IntTooBig(durationMs)) {
        const threadId = window.setTimeout(
          () => {
            this.signOut();
          },
          durationMs,
        );
        _.set(window, 'autoLogoutThread', threadId);
      }
    },

    // spawnAutoRenewForActivityThread spawns an automatic session renegotiating
    // thread that renegotiates a session 24 hours or 90 days into the future if
    // the user has interacted with the app in the last hour minutes, every 1
    // hour.
    spawnAutoRenewForActivityThread() {
      const threadId = setInterval(async () => {
        const now = moment().utc();

        // tsString is a timestamp representing the last time of user
        // interaction in any tab of this browser session.
        const tsString = window.localStorage.getItem(
          LOCAL_STORAGE_ANTICIPATED_LOGOUT_TIME_KEY);
        const ts = parseInt(tsString, 10);
        const hourAgo = now.unix() - (60 * 60);
        if (ts > hourAgo) {
          let secondsToAdd = (24 * 60 * 60); // 1 day
          const stayLoggedIn = auth.StayLoggedInFetch();
          if (stayLoggedIn) {
            secondsToAdd = (90 * 24 * 60 * 60); // 90 days
          }
          const expires = now.clone().add(secondsToAdd, 'seconds');

          let hasError = false;
          let loginResp;
          try {
            loginResp = await api.GenerateFreshJWT(expires);
          } catch (err) {
            if (api.IsAuthFailureError(err)) {
              auth.TokenPurge();
              throw Error(`Could not generate new token (${String(err)})`);
            }
            hasError = true;
          }
          if (!hasError && _.isNil(loginResp)) {
            auth.TokenPurge();
            throw Error('Could not generate new token (nil token returned)');
          }
          if (!_.isNil(loginResp)) {
            auth.TokenStore(loginResp);
            await this.$store.dispatch('SetAuthToken', loginResp);
          }
        }
      }, 15 * 60 * 1000);
      _.set(window, 'autoLogoutRenewForActivityThread', threadId);
    },
  },

  mounted() {
    // Setup inactivity monitors with debounced activity events.
    const activityDebounceMs = ACTIVITY_DEBOUNCE_MS;
    const spawnAutoInactivityLogoutThread = _.debounce(
      this.spawnAutoInactivityLogoutThread,
      activityDebounceMs,
      { leading: true });
    document.onload = spawnAutoInactivityLogoutThread;
    document.onmousedown = spawnAutoInactivityLogoutThread;
    document.onmousemove = spawnAutoInactivityLogoutThread;
    document.ontouchstart = spawnAutoInactivityLogoutThread;
    document.onclick = spawnAutoInactivityLogoutThread;
    document.onkeydown = spawnAutoInactivityLogoutThread;
    document.addEventListener('scroll', spawnAutoInactivityLogoutThread, true);

    // If the page for some reason gets refreshed in the background, make sure
    // the time starts again after QReserve is loaded. TODO: Less hacky solution
    const firstRunner = async () => {
      await this.sleep(10000);
      this.spawnAutoInactivityLogoutThread();
    };
    firstRunner();
  },
};
